import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentImageBA, DynamicObjectInterface } from '../../sdk/type';
import ChangeButton from '../../assets/image/icons/change_button.svg';
import RemoveButton from '../../assets/image/icons/remove_button.svg';
import { AssessmentDetail } from '../../ui/app-components/AssessmentDetail/assessmentDetail';
import {
  Button,
  ButtonStyles,
  ButtonWidth,
  Checkbox,
  Drawer,
  ImageEditorSize,
  Modal,
  TabsComponent,
  Theme,
  useBreakpoint,
} from '../../ui/components';
import { TabType } from '../../ui/components/TabComponent/TabsComponent';

import ImageEditor from '../../ui/components/ImageEditor/ImageEditor';
import { customEventPropType } from '../../ui/components/ImageEditor/types/propsType';
import { resolveProfileImageSectionShown } from '../../ui/utils';
import { UserData } from './dashboardConfig';
import styles from './Dashboard.module.scss';
export interface DetailsModalProps {
  onSave?: DynamicObjectInterface;
  onCancel?: DynamicObjectInterface;
  data: UserData | null;
  handleOnChange?: (value: DynamicObjectInterface, key: string, ...paths: string[]) => void;
  formData?: UserData | null;
  selectedTab?: string;
  // Tab the Result modal should open on ('weightIn' = pre, 'weightOut' = post).
  initialTab?: string;
  campaign?: DynamicObjectInterface;
  validity?: number;
  postAssessmentReminderDays?: number;
  images?: Record<string, AttachmentImageBA>;
  handleFileImage?: (e: customEventPropType, view: string, isBefore: boolean) => void;
  isTablet?: boolean;
}

export const ResultModal = ({
  onSave,
  onCancel,
  data,
  formData,
  handleOnChange = () => {},
  campaign,
  images,
  handleFileImage = () => {},
  initialTab = 'weightIn',
}: DetailsModalProps) => {
  const { t } = useTranslation();
  // Resolve the Front/Side Profile Image Section flag: campaign-level for SINGLE,
  // the participant's own sub-challenge for MULTI. Drives whether saving also
  // uploads the before/after profile images.
  const profileImageSectionShown = resolveProfileImageSectionShown(campaign?.configJson, data?.subChallengeId);
  // Open on the tab the caller requested (pre/post). The modal is only mounted
  // while open, so initializing state from initialTab is enough.
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [carouselType, setCarouselType] = useState('');
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;
  const isTablet = viewPortType === 'md';
  // Phones: drop "Details" from the tab labels so "Pre-Assessment" / "Post-Assessment"
  // fit without overflowing/clipping.
  const isPhone = ['xs', 'sm'].indexOf(viewPortType) !== -1;
  const preTabTitle = isPhone
    ? t('dashboard.my-progress.preAssessment')
    : t('dashboard.my-progress.preAssessmentTitle');
  const postTabTitle = isPhone
    ? t('dashboard.my-progress.postAssessment')
    : t('dashboard.my-progress.postAssessmentTitle');

  const handleTabClick = (tabId: string) => {
    setSelectedTab(tabId);
  };

  const handleImageUpload = (type: string) => {
    setCarouselType(type);
  };

  const handleFile = (e: ChangeEvent<HTMLInputElement>, key: string, isBefore: boolean) => {
    if (e?.target?.files) {
      handleFileImage(
        {
          ...e,
          files: { 0: e?.target?.files?.[0] },
        },
        key,
        isBefore,
      );
    }
  };

  const handleRemoveImage = (e: DynamicObjectInterface, key: string, isBefore: boolean) => {
    handleFileImage(
      {
        ...e,
        files: {},
      },
      key,
      isBefore,
    );
  };

  const validateForm = (carouselType: string, images: DynamicObjectInterface) => {
    let disabled = true;
    if (carouselType === 'weightIn') {
      disabled = !images?.before_front.url || !images?.before_side.url || !formData?.beforeAndAfterCheckbox;
    } else if (carouselType === 'weightOut') {
      disabled = !images?.after_front.url || !images?.after_side.url || !formData?.beforeAndAfterCheckbox;
    }
    return disabled;
  };

  const beforeAndAfterCancel = (
    <Button
      title={t('dashboard.my-progress.button.cancel')}
      buttonStyle={ButtonStyles.OUTLINE}
      onClick={() => setCarouselType('')}
      buttonWidth={ButtonWidth.FULL_WIDTH}
      theme={Theme.GREEN}
    />
  );

  const beforeAndAfterConfirm = (
    <Button
      title={t('dashboard.my-progress.button.confirm')}
      onClick={() => onSave(formData)}
      disabled={validateForm(carouselType, images)}
      buttonWidth={ButtonWidth.FULL_WIDTH}
      theme={Theme.GREEN}
    />
  );

  const header = (
    <div className={styles['assessment-title-wrapper']}>
      <div className={styles['assessment-title']}>{t('dashboard.my-progress.assessmentUploadPhotoTitle')}</div>
      <div className={styles['assessment-subtitle']}>{t('dashboard.my-progress.assessmentUploadPhotoDesc')}</div>
    </div>
  );

  const beforeAndAfterContent = (
    <div className={styles['modal-upload']}>
      {carouselType === 'weightIn' && (
        <div className={styles['before-container']}>
          {header}
          <div className={styles['upload-container']}>
            <div className={styles['image-container']}>
              {images?.before_front.url ? (
                <div className={styles['image-wrapper']} style={{ position: 'relative' }}>
                  <img src={images.before_front.url} alt="Before Front View" className={styles['uploaded-image']} />
                  <div className={styles['buttons-container']}>
                    <button
                      className={styles['change-photo']}
                      onClick={() => document.getElementById('before_front')?.click()}
                    >
                      <img src={ChangeButton} alt="bg" />
                      <input
                        type="file"
                        id="before_front"
                        accept="image/*"
                        onChange={e => handleFile(e, 'front', true)}
                        style={{ display: 'none' }}
                      />
                    </button>
                    <button className={styles['remove-photo']} onClick={e => handleRemoveImage(e, 'front', true)}>
                      <img src={RemoveButton} alt="bg" />
                    </button>
                  </div>
                </div>
              ) : (
                <ImageEditor
                  onFileUpload={e => handleFileImage(e, 'front', true)}
                  accept="image/*"
                  imageEditorSize={ImageEditorSize.FIXED_SIZE}
                  uploadCTALabel={t('dashboard.my-progress.assessmentUploadFile')}
                  fileSizeLimitLabel={t('dashboard.my-progress.assessmentFileSizeLimitLabel')}
                />
              )}
              <div className={styles['label']}>{t('dashboard.my-progress.assessmentFrontView')}</div>
            </div>
            <div className={styles['image-container']}>
              {images?.before_side.url ? (
                <div className={styles['image-wrapper']} style={{ position: 'relative' }}>
                  <img src={images.before_side.url} alt="Before Side View" className={styles['uploaded-image']} />
                  <div className={styles['buttons-container']}>
                    <button
                      className={styles['change-photo']}
                      onClick={() => document.getElementById('before_side')?.click()}
                    >
                      <img src={ChangeButton} alt="bg" />
                      <input
                        type="file"
                        id="before_side"
                        accept="image/*"
                        onChange={e => handleFile(e, 'side', true)}
                        style={{ display: 'none' }}
                      />
                    </button>
                    <button className={styles['remove-photo']} onClick={e => handleRemoveImage(e, 'side', true)}>
                      <img src={RemoveButton} alt="bg" />
                    </button>
                  </div>
                </div>
              ) : (
                <ImageEditor
                  onFileUpload={e => handleFileImage(e, 'side', true)}
                  accept="image/*"
                  imageEditorSize={ImageEditorSize.FIXED_SIZE}
                  uploadCTALabel={t('dashboard.my-progress.assessmentUploadFile')}
                  fileSizeLimitLabel={t('dashboard.my-progress.assessmentFileSizeLimitLabel')}
                />
              )}
              <div className={styles['label']}>{t('dashboard.my-progress.assessmentSideView')}</div>
            </div>
          </div>
          <Checkbox
            key={`checkbox_${selectedTab}`}
            id={`checkbox_${selectedTab}`}
            theme={Theme.BLACK}
            checked={formData?.beforeAndAfterCheckbox}
            onChange={e => handleOnChange(!formData?.beforeAndAfterCheckbox, 'beforeAndAfterCheckbox')}
            text={t('dashboard.my-progress.assessmentProfileImageCheckbox')}
          />
        </div>
      )}
      {carouselType === 'weightOut' && (
        <>
          <div className={styles['before-container']}>
            {header}
            <div className={styles['upload-container']}>
              <div className={styles['image-container']}>
                {images?.after_front.url ? (
                  <div className={styles['image-wrapper']} style={{ position: 'relative' }}>
                    <img src={images.after_front.url} alt="After Front View" className={styles['uploaded-image']} />
                    <div className={styles['buttons-container']}>
                      <button
                        className={styles['change-photo']}
                        onClick={() => document.getElementById('after_front')?.click()}
                      >
                        <img src={ChangeButton} alt="bg" />
                        <input
                          type="file"
                          id="after_front"
                          accept="image/*"
                          onChange={e => handleFile(e, 'front', false)}
                          style={{ display: 'none' }}
                        />
                      </button>
                      <button className={styles['remove-photo']} onClick={e => handleRemoveImage(e, 'front', false)}>
                        <img src={RemoveButton} alt="bg" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <ImageEditor
                    onFileUpload={e => handleFileImage(e, 'front', false)}
                    accept="image/*"
                    imageEditorSize={ImageEditorSize.FIXED_SIZE}
                    uploadCTALabel={t('dashboard.my-progress.assessmentUploadFile')}
                    fileSizeLimitLabel={t('dashboard.my-progress.assessmentFileSizeLimitLabel')}
                  />
                )}
                <div className={styles['label']}>{t('dashboard.my-progress.assessmentFrontView')}</div>
              </div>
              <div className={styles['image-container']}>
                {images?.after_side.url ? (
                  <div className={styles['image-wrapper']} style={{ position: 'relative' }}>
                    <img src={images.after_side.url} alt="After Side View" className={styles['uploaded-image']} />
                    <div className={styles['buttons-container']}>
                      <button
                        className={styles['change-photo']}
                        onClick={() => document.getElementById('after_side')?.click()}
                      >
                        <img src={ChangeButton} alt="bg" />
                        <input
                          type="file"
                          id="after_side"
                          accept="image/*"
                          onChange={e => handleFile(e, 'side', false)}
                          style={{ display: 'none' }}
                        />
                      </button>
                      <button className={styles['remove-photo']} onClick={e => handleRemoveImage(e, 'side', false)}>
                        <img src={RemoveButton} alt="bg" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <ImageEditor
                    onFileUpload={e => handleFileImage(e, 'side', false)}
                    accept="image/*"
                    imageEditorSize={ImageEditorSize.FIXED_SIZE}
                    uploadCTALabel={t('dashboard.my-progress.assessmentUploadFile')}
                    fileSizeLimitLabel={t('dashboard.my-progress.assessmentFileSizeLimitLabel')}
                  />
                )}
                <div className={styles['label']}>{t('dashboard.my-progress.assessmentSideView')}</div>
              </div>
            </div>
          </div>
          <Checkbox
            key={`checkbox_${selectedTab}`}
            id={`checkbox_${selectedTab}`}
            theme={Theme.BLACK}
            checked={formData?.beforeAndAfterCheckbox}
            onChange={e => handleOnChange(!formData?.beforeAndAfterCheckbox, 'beforeAndAfterCheckbox')}
            text={t('dashboard.my-progress.assessmentProfileImageCheckbox')}
          />
        </>
      )}
    </div>
  );

  return (
    <>
      {isDesktopView ? (
        <>
          {formData?.teamIncomplete ? (
            <Modal headline={t('dashboard.my-progress.incompleteTeamTitle')} closeIcon={false} containerWidth={'25%'}>
              <div className={styles['incomplete-team-modal']}>
                <div>{t('dashboard.my-progress.incompleteTeamDesc')}</div>
                <div className={styles['incomplete-team-modal-button-container']}>
                  <Button
                    title={`${t('dashboard.my-progress.button.ok')}`}
                    buttonStyle={ButtonStyles.SOLID}
                    theme={Theme.GREEN}
                    onClick={onCancel}
                    buttonWidth={ButtonWidth.FIT_CONTENT}
                  />
                </div>
              </div>
            </Modal>
          ) : (
            <Modal
              headline={''}
              containerWidth={!carouselType ? '60%' : 'fit-content'}
              containerHeight={!carouselType ? '80vh' : undefined}
              primaryButton={carouselType ? beforeAndAfterConfirm : undefined}
              secondaryButton={carouselType ? beforeAndAfterCancel : undefined}
              closeIcon={true}
              onClose={onCancel}
            >
              {!carouselType ? (
                <div className={styles['assessment-container']}>
                  {/* Pre / Post are now split into tabs (per UIUX) instead of shown
                      side-by-side, matching the mobile layout. */}
                  <TabsComponent
                    theme={Theme.PURPLE}
                    tabList={[
                      {
                        title: preTabTitle,
                        value: 'Pre-Assessment Details',
                        id: 'weightIn',
                      },
                      {
                        title: postTabTitle,
                        value: 'Post-Assessment Details',
                        id: 'weightOut',
                      },
                    ]}
                    onTabClick={handleTabClick}
                    sortAndFilter={false}
                    selectedTab={selectedTab}
                    tabType={TabType.COMPONENT}
                  />
                  <AssessmentDetail
                    selectedTab={selectedTab}
                    validity={parseInt(campaign?.configJson?.activities?.participant?.validity?.value)}
                    postAssessmentReminderDays={parseInt(campaign?.configJson?.postAssessmentReminderDays)}
                    onSave={
                      profileImageSectionShown
                        ? () => handleImageUpload(selectedTab)
                        : () => onSave(formData)
                    }
                    data={data}
                    formData={formData}
                    handleOnChange={handleOnChange}
                    onCancel={onCancel}
                    images={images}
                    isTablet={isTablet}
                  />
                </div>
              ) : (
                <>{beforeAndAfterContent}</>
              )}
            </Modal>
          )}
        </>
      ) : (
        <>
          {formData?.teamIncomplete ? (
            <Drawer bottomGap={0}>
              <div className={styles['drawer-header']}>{t('dashboard.my-progress.incompleteTeamTitle')}</div>
              <div className={styles['incomplete-team-modal']}>
                <div>{t('dashboard.my-progress.incompleteTeamDesc')}</div>
                <div className={styles['incomplete-team-modal-button-container']}>
                  <Button
                    title={`${t('dashboard.my-progress.button.ok')}`}
                    buttonStyle={ButtonStyles.SOLID}
                    theme={Theme.GREEN}
                    onClick={onCancel}
                    buttonWidth={ButtonWidth.FIT_CONTENT}
                  />
                </div>
              </div>
            </Drawer>
          ) : (
            <Drawer
              contentHeight={'91vh'}
              onClose={onCancel}
              bottomGap={0}
              footer={
                !!carouselType && (
                  <div className={styles['assessment-detail-buttons']}>
                    {beforeAndAfterCancel}
                    {beforeAndAfterConfirm}
                  </div>
                )
              }
            >
              {!carouselType ? (
                <>
                  <TabsComponent
                    theme={Theme.PURPLE}
                    tabList={[
                      {
                        title: preTabTitle,
                        value: 'Pre-Assessment Details',
                        id: 'weightIn',
                      },
                      {
                        title: postTabTitle,
                        value: 'Post-Assessment Details',
                        id: 'weightOut',
                      },
                    ]}
                    onTabClick={handleTabClick}
                    sortAndFilter={false}
                    selectedTab={selectedTab}
                    tabType={TabType.COMPONENT}
                  />
                  <AssessmentDetail
                    selectedTab={selectedTab}
                    validity={parseInt(campaign?.configJson?.activities?.participant?.validity?.value)}
                    postAssessmentReminderDays={parseInt(campaign?.configJson?.postAssessmentReminderDays)}
                    onSave={
                      profileImageSectionShown
                        ? () => handleImageUpload(selectedTab)
                        : () => onSave(formData)
                    }
                    data={data}
                    formData={formData}
                    handleOnChange={handleOnChange}
                    onCancel={onCancel}
                    images={images}
                    isTablet={isTablet}
                  />
                </>
              ) : (
                <>{beforeAndAfterContent}</>
              )}
            </Drawer>
          )}
        </>
      )}
    </>
  );
};
